import { useEffect } from "react";
import gsap from "gsap";

/**
 * Drives the boot-splash focus-pull sequence: small+blurred -> sharp
 * gold-lit focus -> a quiet breath -> zoom-through exit, crossfading
 * the app shell in underneath on the same timeline (so there's no
 * hard cut between splash.remove() and the app appearing).
 *
 * Mirrors the standalone splash.html timeline exactly (~2.7s total).
 */
export default function useSplashAnimation({
  glowRef,
  ringRef,
  flashRef,
  brandZoomRef,
  shineRef,
  appRef,
  onFinish,
}) {
  useEffect(() => {
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduceMotion) {
      if (appRef.current) appRef.current.style.opacity = 1;
      onFinish?.();
      return;
    }

    const tl = gsap.timeline({ onComplete: () => onFinish?.() });

    // ENTRANCE — focus pull.
    tl.to(glowRef.current, { opacity: 1, scale: 1, duration: 1.18, ease: "sine.out" }, 0)
      .to(ringRef.current, { opacity: 0.7, scale: 1, duration: 0.76, ease: "power2.out" }, 0.04)
      .to(ringRef.current, { opacity: 0, duration: 0.34, ease: "power1.out" }, 0.51)
      .to(
        brandZoomRef.current,
        { scale: 1, opacity: 1, filter: "blur(0px)", duration: 0.97, ease: "expo.out" },
        0
      );

    // Gold light arriving with the focus pull.
    tl.fromTo(
      flashRef.current,
      { opacity: 0, scale: 0.3 },
      { opacity: 0.6, scale: 1, duration: 0.42, ease: "sine.out" },
      0.3
    )
      .to(flashRef.current, { opacity: 0, scale: 1.7, duration: 0.51, ease: "sine.in" }, 0.68)
      .fromTo(
        shineRef.current,
        { xPercent: -140 },
        { xPercent: 380, duration: 0.72, ease: "power2.inOut" },
        0.42
      );

    // A single quiet breath once the mark is in focus.
    tl.to(brandZoomRef.current, { scale: 1.018, duration: 0.38, ease: "sine.inOut" }, 1.01).to(
      brandZoomRef.current,
      { scale: 1, duration: 0.38, ease: "sine.inOut" },
      1.39
    );

    // Hold
    tl.to({}, { duration: 0.13 }, 1.77);

    // EXIT — push through and blur out; the app shell crossfades in underneath.
    tl.to(
      brandZoomRef.current,
      { scale: 5.5, opacity: 0, filter: "blur(24px)", duration: 0.63, ease: "power2.in" },
      1.9
    )
      .to(glowRef.current, { opacity: 0, scale: 1.6, duration: 0.51, ease: "power1.in" }, 1.9)
      .to(appRef.current, { opacity: 1, duration: 0.59, ease: "power2.out" }, 2.11);

    return () => {
      tl.kill();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
